import React, { useContext } from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import ThemeContext from '../utils/theming';

const Container = styled.div`
    display: flex;
    flex-direction: row;
    flex: 1;
    padding: ${props => (props.sidebar ? `0` : `12px 0`)};
`;

const Checkbox = styled.input`
    cursor: pointer;
    margin-bottom: 1.16em;
    margin-top: -0.3em;
    display: none;

    + label {
        cursor: pointer;
    }

    :checked + label::before {
        background: ${({ base64String }) => `url(${base64String})`};
    }

    + label::before {
        background: url('data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMzIgMzIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPjx0aXRsZT5DaGVjazwvdGl0bGU+PGRlZnM+PGNpcmNsZSBpZD0iYSIgY3g9IjE2IiBjeT0iMTYiIHI9IjE2Ii8+PG1hc2sgaWQ9ImIiIHg9IjAiIHk9IjAiIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgZmlsbD0iI2ZmZiI+PHVzZSB4bGluazpocmVmPSIjYSIvPjwvbWFzaz48L2RlZnM+PHVzZSBtYXNrPSJ1cmwoI2IpIiB4bGluazpocmVmPSIjYSIgc3Ryb2tlLXdpZHRoPSI2IiBzdHJva2U9IiNFMEUwRTAiIGZpbGw9Im5vbmUiIGZpbGwtcnVsZT0iZXZlbm9kZCIvPjwvc3ZnPg==');
    }

    + label::before {
        content: '';
        width: 16px;
        height: 16px;
        margin-right: 0.75em;
        position: absolute;
        left: 0;
        top: 0;
    }
`;

const Label = styled.label`
    font-family: Averta;
    font-size: 0.5em;
    font-weight: bold;
    line-height: 16px;
    letter-spacing: 1px;
    ${props => props.uppercase && 'text-transform: uppercase;'}
    margin-bottom: 0.5em;
    padding-left: 22px;
    position: relative;

    .text {
        line-height: 12px;
        display: inline-block;
    }
`;

const CheckboxGroup = props => {
    const theme = useContext(ThemeContext);
    const checkBoxSVGString = `
    <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
        <title>Check Box</title>
        <g fill="none" fill-rule="evenodd">
            <circle fill="${theme.colors.primary}" cx="16" cy="16" r="16"/>
            <path d="M14.646 19.86l-3.5-3.418c-.195-.188-.195-.494 0-.684l.707-.683c.195-.188.512-.188.707 0L15 17.467l5.44-5.325c.194-.188.51-.188.706 0l.707.684c.195.188.195.495 0 .682l-6.5 6.35c-.195.19-.512.19-.707 0" fill="#FFF"/>
        </g>
    </svg>
    `;
    let base64String =
        'PHN2ZyB2aWV3Qm94PSIwIDAgMzIgMzIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHRpdGxlPkNoZWNrIEJveDwvdGl0bGU+PGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj48Y2lyY2xlIGZpbGw9IiNmZjI0NWUiIGN4PSIxNiIgY3k9IjE2IiByPSIxNiIvPjxwYXRoIGQ9Ik0xNC42NDYgMTkuODZsLTMuNS0zLjQxOGMtLjE5NS0uMTg4LS4xOTUtLjQ5NCAwLS42ODRsLjcwNy0uNjgzYy4xOTUtLjE4OC41MTItLjE4OC43MDcgMEwxNSAxNy40NjdsNS40NC01LjMyNWMuMTk0LS4xODguNTEtLjE4OC43MDYgMGwuNzA3LjY4NGMuMTk1LjE4OC4xOTUuNDk1IDAgLjY4MmwtNi41IDYuMzVjLS4xOTUuMTktLjUxMi4xOS0uNzA3IDAiIGZpbGw9IiNGRkYiLz48L2c+PC9zdmc+';
    if (typeof window.btoa === 'function') {
        base64String = window.btoa(checkBoxSVGString);
    }

    return (
        <Container sidebar={props.sidebar} style={props.style}>
            <Checkbox
                base64String={`data:image/svg+xml;base64,${base64String}`}
                onClick={props.onClick || null}
                id={props.input.name}
                {...props.input}
                type="checkbox"
                checked={
                    props.input.value || (props.meta.pristine && props.defaultChecked)
                }
            />
            <Label
                htmlFor={props.input.name}
                uppercase={!props.keepExistingCapitalization}
            >
                <span className="text">
                    {props.label}
                    {props.link}
                </span>
            </Label>
        </Container>
    );
};

CheckboxGroup.propTypes = {
    defaultChecked: PropTypes.bool,
    input: PropTypes.object.isRequired,
    keepExistingCapitalization: PropTypes.bool,
    label: PropTypes.string.isRequired,
    link: PropTypes.element,
    meta: PropTypes.object.isRequired,
    onClick: PropTypes.func,
    primary: PropTypes.bool,
    sidebar: PropTypes.bool,
    style: PropTypes.object
};

export default CheckboxGroup;
